import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  IconButton,
  Divider,
  Collapse,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Menu,
  Button,
  Badge,
  Snackbar,
  Alert,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Notifications as NotificationsIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  HelpOutline as HelpIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import OnboardingTour from './OnboardingTour';
import GlobalSearch from './GlobalSearch';
import { useStandard } from '../contexts/StandardContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { NAV_ITEMS, PORTAL_CONFIG, resolvePortalKey } from '../config/navigation';
import api from '../services/api';
import SidebarCustomizationDialog from './SidebarCustomizationDialog';
import { Settings as SettingsIcon, Visibility as VisibilityIcon } from '@mui/icons-material';

const drawerWidth = 280;
const collapsedDrawerWidth = 72;

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasAnyPermission } = useAuth();
  const { organization } = useTenant();
  const { availableStandards, selectedStandard, setSelectedStandard } = useStandard();
  const { mode, toggleTheme } = useThemeMode();
  const { restartTour } = useOnboarding();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [customizerOpen, setCustomizerOpen] = useState(false);
  const [sidebarPreferences, setSidebarPreferences] = useState({});
  const [setupAlertOpen, setSetupAlertOpen] = useState(false);

  // Notification State
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const openNotifMenu = Boolean(notifAnchorEl);
  const [notifications, setNotifications] = useState([]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const fetchNotifications = React.useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  }, []);

  React.useEffect(() => {
    if (user) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  // Update favicon when organization logo changes
  React.useEffect(() => {
    if (organization?.logo_url) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = organization.logo_url;
      document.getElementsByTagName('head')[0].appendChild(link);

      // Also update the page title
      document.title = organization.name || 'ISO Compliance Platform';
    }
  }, [organization]);


  const handleNotificationClick = async (notif) => {
    setNotifAnchorEl(null);
    if (!notif.is_read) {
      try {
        await api.put(`/notifications/${notif.id}/read`);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      } catch (e) { console.error(e); }
    }
    if (notif.link) {
      navigate(notif.link);
    }
  };

  const handleToggleItem = (key) => {
    setExpandedItems((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotifMenuOpen = (event) => {
    setNotifAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleNotifMenuClose = () => {
    setNotifAnchorEl(null);
  };

  const getGuideInfo = () => {
    const path = location.pathname;
    if (path.includes('/app/documents')) return { label: 'Documents Guide', key: 'documents' };
    if (path.includes('/app/audits')) return { label: 'Audits Guide', key: 'audits' };
    if (path.includes('/app/capa')) return { label: 'CAPA Guide', key: 'capa' };
    if (path.includes('/app/risks')) return { label: 'Risks Guide', key: 'risks' };
    if (path.includes('/app/users')) return { label: 'Employees Guide', key: 'users' };
    if (path.includes('/app/process-improvement')) return { label: 'Improvement Guide', key: 'tasks' };
    if (path.includes('/app/nonconformities')) return { label: 'Nonconformities Guide', key: 'nonconformities' };
    if (path.includes('/app/iso-clauses')) return { label: 'ISO Clauses Guide', key: 'iso_clauses' };
    if (path.includes('/app/change-management')) return { label: 'Change Guide', key: 'change_management' };
    if (path.includes('/app/quality-alerts')) return { label: 'Quality Alerts Guide', key: 'quality_alerts' };
    if (path.includes('/app/anonymous-reports')) return { label: 'Anonymous Guide', key: 'anonymous_reporting' };
    if (path.includes('/app/complaints')) return { label: 'Complaints Guide', key: 'customer_complaints' };
    if (path.includes('/app/objectives')) return { label: 'Objectives Guide', key: 'quality_objectives' };
    if (path.includes('/app/management-reviews')) return { label: 'Reviews Guide', key: 'management_reviews' };
    if (path.includes('/app/training-management')) return { label: 'Training Guide', key: 'training_management' };
    if (path.includes('/app/permissions')) return { label: 'Permissions Guide', key: 'permissions' };
    if (path.includes('/app/org-processes')) return { label: 'Mapping Guide', key: 'departments' };
    if (path.includes('/app/departments')) return { label: 'Departments Guide', key: 'teams' };
    if (path.includes('/app/organizational-chart')) return { label: 'Org Chart Guide', key: 'org_chart' };
    if (path.includes('/app/jobs/positions')) return { label: 'Positions Guide', key: 'job_positions' };
    if (path.includes('/app/jobs/libraries')) return { label: 'Competencies Guide', key: 'job_libraries' };
    if (path.includes('/app/task-templates')) return { label: 'Templates Guide', key: 'task_templates' };
    if (path.includes('/app/jobs/analytics')) return { label: 'Analytics Guide', key: 'job_analytics' };
    if (path.includes('/app/settings')) return { label: 'Settings Guide', key: 'settings' };
    if (path.includes('/app/support-tickets')) return { label: 'Support Guide', key: 'support_tickets' };
    return { label: 'Platform Guide', key: 'platform_guide' };
  };

  const guideInfo = getGuideInfo();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSaveSidebarPreferences = async (prefs) => {
    setSidebarPreferences(prefs);
    if (user?.id) {
      localStorage.setItem(`sidebar_preferences_${user.id}`, JSON.stringify(prefs));
    }

    // Persist to Backend if user has permission
    if (user?.role === 'org_admin' || user?.role === 'compliance_manager') {
      try {
        await api.put('/organizations/me', {
          settings: {
            sidebar_preferences: prefs
          }
        });
      } catch (err) {
        console.error('Failed to save sidebar preferences to server:', err);
      }
    }
  };


  const portalKey = resolvePortalKey(user);
  const portalConfig = PORTAL_CONFIG[portalKey] || PORTAL_CONFIG.default;

  // Load and Sync sidebar preferences
  React.useEffect(() => {
    if (!user) return;

    const storageKey = `sidebar_preferences_${user.id}`;
    const localPrefs = JSON.parse(localStorage.getItem(storageKey) || '{}');

    // Only admins sync with server settings
    if (user.role === 'org_admin' && user?.organization?.settings?.sidebar_preferences) {
      const serverPrefs = user.organization.settings.sidebar_preferences;
      // Simple merge: server wins if keys exist
      const merged = { ...localPrefs, ...serverPrefs };

      // Update local storage if merged is different to keep in sync
      if (JSON.stringify(merged) !== JSON.stringify(localPrefs)) {
        localStorage.setItem(storageKey, JSON.stringify(merged));
      }
      setSidebarPreferences(merged);
    } else {
      // Regular users just use their local isolation
      setSidebarPreferences(localPrefs);
    }
  }, [user]);

  // Enforce onboarding: redirect org_admin to dashboard if setup not completed
  React.useEffect(() => {
    if (user?.role === 'org_admin' && (!user?.organization || !user.organization.setup_completed)) {
      if (location.pathname !== '/app/dashboard' && location.pathname !== '/app') {
        navigate('/app/dashboard', { replace: true });
      }
    }
  }, [user, location.pathname, navigate]);

  const menuGroups = React.useMemo(() => {
    // If Admin and setup NOT complete, hide everything
    // We check explicitly for false or undefined (since migration adds default false)
    if (user?.role === 'org_admin' && (!user?.organization || !user.organization.setup_completed)) {
      // Return empty or restricted list
      return [];
    }

    return portalConfig.menuGroups
      .map((group) => {
        // Filter out groups that are hidden via preferences
        if (sidebarPreferences[group.label] === false) {
          return null;
        }

        const items = group.items
          .map((key) => NAV_ITEMS[key])
          .filter(Boolean)
          .filter((item) => {
            // Filter by standard if specified
            if (item.standards && selectedStandard && !item.standards.includes(selectedStandard)) {
              return false;
            }

            if (item.hideForRoles && item.hideForRoles.includes(user?.role)) return false;
            if (!item.permissions || item.permissions.length === 0) return true;
            const hasPerm = hasAnyPermission(item.permissions);
            return hasPerm;
          })
          .filter((item) => {
            // Filter out individual items that are hidden via preferences
            const itemVisibilityKey = `${group.label}:${item.key}`;
            return sidebarPreferences[itemVisibilityKey] !== false;
          });

        return {
          label: group.label,
          items: items
        };
      })
      .filter((group) => group && group.items.length > 0);
  }, [portalConfig, hasAnyPermission, selectedStandard, sidebarPreferences, user]);

  // Initialize expanded groups - only runs when menuGroups structure effectively changes
  React.useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;

      menuGroups.forEach(({ label }) => {
        if (typeof next[label] === 'undefined') {
          next[label] = true;
          changed = true;
        }
      });

      // Cleanup removed groups
      Object.keys(next).forEach((key) => {
        if (!menuGroups.find((group) => group.label === key)) {
          delete next[key];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [menuGroups]);

  const toggleGroup = (label) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  // Hide all Layout chrome (sidebar, appbar) when org_admin hasn't completed setup wizard
  const isSetupPending = user?.role === 'org_admin' && (!user?.organization || !user.organization.setup_completed);
  if (isSetupPending) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Outlet />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh' }}>
      <OnboardingTour />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: mode === 'dark' ? alpha('#0f172a', 0.8) : alpha('#fff', 0.8),
          backdropFilter: 'blur(30px)',
          borderBottom: '1px solid',
          borderColor: mode === 'dark' ? alpha('#fff', 0.08) : alpha('#000', 0.05),
          boxShadow: mode === 'dark' ? '0 10px 40px -10px rgba(0,0,0,0.5)' : '0 10px 40px -10px rgba(0,0,0,0.05)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Toolbar sx={{ px: 2 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={toggleSidebar}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Company Logo and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            {organization?.logo_url && (
              <Box
                component="img"
                src={organization.logo_url}
                alt={organization.name}
                sx={{
                  height: { xs: 32, sm: 40 },
                  width: 'auto',
                  maxWidth: { xs: 120, sm: 150 },
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            )}
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 900,
                letterSpacing: '-0.04em',
                background: mode === 'dark'
                  ? 'linear-gradient(135deg, #fff 0%, #cbd5e1 100%)'
                  : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: organization?.logo_url ? { xs: 'none', md: 'block' } : 'block'
              }}
            >
              {organization?.name || 'ISO Compliance Platform'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {availableStandards.length > 0 && (
              availableStandards.length === 1 ? (
                <Chip
                  label={availableStandards[0].toUpperCase()}
                  color="primary"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              ) : (
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={selectedStandard || availableStandards[0]}
                    onChange={(e) => setSelectedStandard(e.target.value)}
                    displayEmpty
                  >
                    {availableStandards.map((standard) => (
                      <MenuItem key={standard} value={standard}>
                        {standard.toUpperCase()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            )}
            <IconButton onClick={() => setSearchOpen(true)} id="global-search-btn" sx={{ color: 'text.secondary' }}>
              <SearchIcon />
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              onClick={() => restartTour(guideInfo.key)}
              startIcon={<HelpIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                display: { xs: 'none', lg: 'flex' },
                bgcolor: mode === 'dark' ? alpha('#4F46E5', 0.1) : alpha('#4F46E5', 0.05),
                borderColor: alpha('#4F46E5', 0.5),
                color: 'primary.main',
                '&:hover': {
                  bgcolor: alpha('#4F46E5', 0.2),
                  borderColor: 'primary.main',
                }
              }}
            >
              {guideInfo.label}
            </Button>
            <IconButton onClick={toggleTheme} sx={{ color: 'text.secondary' }}>
              {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>

            {/* Notifications */}
            <IconButton onClick={handleNotifMenuOpen} sx={{ color: 'text.secondary' }}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={notifAnchorEl}
              open={openNotifMenu}
              onClose={handleNotifMenuClose}
              onClick={handleNotifMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  minWidth: 280,
                  maxWidth: 360,
                  maxHeight: 400,
                  overflowY: 'auto',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">Notifications</Typography>
              </Box>
              {notifications.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="caption">No notifications</Typography>
                </Box>
              )}
              {notifications.map(notif => (
                <MenuItem key={notif.id} onClick={(e) => { e.stopPropagation(); handleNotificationClick(notif); }} sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: notif.is_read ? 'transparent' : 'action.hover', whiteSpace: 'normal' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: notif.is_read ? 400 : 700 }}>{notif.title}</Typography>
                    {!notif.is_read && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {notif.message}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.5 }}>
                    {new Date(notif.created_at).toLocaleString()}
                  </Typography>
                </MenuItem>
              ))}
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
                <Button size="small" onClick={() => navigate('/app/notifications')}>View All</Button>
              </Box>
            </Menu>
            <Button
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textTransform: 'none',
                color: 'inherit',
                borderRadius: 2,
                px: 1
              }}
            >
              <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                  {['org_admin', 'compliance_manager'].includes(user?.role)
                    ? user?.role?.replace('_', ' ')
                    : user?.job_title || user?.job_position?.title || (user?.role === 'org_admin' ? 'Org Admin' : user?.role === 'compliance_manager' ? 'Compliance Manager' : user?.role?.replace('_', ' ') || 'Member')}
                </Typography>
              </Box>
              <Avatar
                src={user?.avatar_url}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
                }}
              >
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </Avatar>
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        open={sidebarOpen}
        sx={{
          width: sidebarOpen ? drawerWidth : collapsedDrawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: sidebarOpen ? drawerWidth : collapsedDrawerWidth,
            boxSizing: 'border-box',
            marginTop: '64px',
            height: 'calc(100vh - 64px)',
            bgcolor: mode === 'dark' ? alpha('#0f172a', 0.6) : alpha('#fff', 0.6),
            backdropFilter: 'blur(30px)',
            borderRight: '1px solid',
            borderColor: mode === 'dark' ? alpha('#fff', 0.05) : alpha('#000', 0.05),
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            overflowY: 'auto',
            padding: 0,
          },
        }}
      >
        <Box id="sidebar-nav" sx={{ overflow: 'auto', py: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {sidebarOpen && (user?.role === 'org_admin' || user?.role === 'compliance_manager') && (
            <Box sx={{ px: 2, mb: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon sx={{ fontSize: 18 }} />}
                onClick={() => setCustomizerOpen(true)}
                sx={{
                  justifyContent: 'flex-start',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 1,
                  px: 2,
                  mx: 1,
                  width: 'auto',
                  borderRadius: 3,
                  border: '1px dashed',
                  borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                  bgcolor: mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0,0,0,0.02)',
                  '&:hover': {
                    bgcolor: mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Customize Sidebar
              </Button>
            </Box>
          )}
          <List sx={{ px: sidebarOpen ? 2 : 1, py: 0, flex: '1 1 auto' }}>
            {menuGroups.map((group) => {
              const expanded = expandedGroups[group.label] ?? true;
              return (
                <Box key={group.label} sx={{ mb: 1 }}>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => toggleGroup(group.label)}
                      sx={{
                        borderRadius: 2,
                        minHeight: 40,
                        px: sidebarOpen ? 2 : 1.5,
                        justifyContent: sidebarOpen ? 'space-between' : 'center',
                        color: 'text.secondary',
                        mb: 0.5,
                        '&:hover': {
                          color: 'text.primary',
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      {sidebarOpen && (
                        <ListItemText
                          primary={group.label}
                          primaryTypographyProps={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            sx: { opacity: 0.8 }
                          }}
                        />
                      )}
                      {expanded ? (
                        <ExpandLessIcon sx={{ fontSize: 16, opacity: 0.5 }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {group.items.map((item) => {
                        const isSelected = location.pathname === item.path;
                        const hasSubItems = item.subItems && item.subItems.length > 0;
                        const isExpanded = expandedItems[item.key] || false;

                        return (
                          <React.Fragment key={item.text}>
                            <ListItem disablePadding sx={{ mb: 0.3, display: 'block' }}>
                              <ListItemButton
                                id={item.key === 'documents' ? 'document-management-nav' :
                                  item.key === 'audits' ? 'audit-management-nav' :
                                    item.key === 'capas' ? 'capa-management-nav' :
                                      item.key === 'settings' ? 'user-settings-menu' : undefined}
                                selected={isSelected}
                                onClick={() => {
                                  if (item.comingSoon) {
                                    setComingSoonOpen(true);
                                  } else if (item.path) {
                                    navigate(item.path);
                                  } else if (hasSubItems) {
                                    handleToggleItem(item.key);
                                  }
                                }}
                                sx={{
                                  minHeight: 48,
                                  mx: 1.5,
                                  my: 0.5,
                                  borderRadius: 2,
                                  pl: sidebarOpen ? 2 : 1.5,
                                  pr: sidebarOpen ? 2 : 1.5,
                                  justifyContent: sidebarOpen ? 'initial' : 'center',
                                  opacity: item.comingSoon ? 0.6 : 1,
                                  cursor: item.comingSoon ? 'not-allowed' : 'pointer',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  background: isSelected
                                    ? (mode === 'dark'
                                      ? 'linear-gradient(90deg, rgba(99, 102, 241, 0.15) 0%, rgba(99, 102, 241, 0.05) 100%)'
                                      : 'linear-gradient(90deg, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0.02) 100%)')
                                    : 'transparent',
                                  border: isSelected
                                    ? (mode === 'dark' ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid rgba(99, 102, 241, 0.2)')
                                    : '1px solid transparent',
                                  boxShadow: isSelected ? '0 4px 12px rgba(99, 102, 241, 0.15)' : 'none',
                                  '&:hover': {
                                    background: isSelected
                                      ? (mode === 'dark' ? 'linear-gradient(90deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0.1) 100%)' : 'rgba(99, 102, 241, 0.12)')
                                      : (mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'),
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
                                  },
                                  transition: 'all 0.2s ease',
                                  '&::before': isSelected ? {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: '15%',
                                    height: '70%',
                                    width: 3,
                                    borderTopRightRadius: 4,
                                    borderBottomRightRadius: 4,
                                    backgroundColor: '#6366f1',
                                    boxShadow: '0 0 8px rgba(99, 102, 241, 0.6)'
                                  } : {}
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: sidebarOpen ? 2 : 'auto',
                                    justifyContent: 'center',
                                    color: isSelected ? 'primary.main' : 'text.secondary',
                                  }}
                                >
                                  {item.icon}
                                </ListItemIcon>
                                {sidebarOpen && (
                                  <>
                                    <ListItemText
                                      primary={item.text}
                                      primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: isSelected ? 600 : 500,
                                        color: isSelected ? 'primary.main' : 'text.primary'
                                      }}
                                    />
                                    {hasSubItems && (
                                      <Box
                                        component="span"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleItem(item.key);
                                        }}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          p: 0.5,
                                          borderRadius: '50%',
                                          color: 'text.secondary',
                                          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' }
                                        }}
                                      >
                                        {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                      </Box>
                                    )}
                                  </>
                                )}
                              </ListItemButton>
                            </ListItem>
                            {
                              hasSubItems && (
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <List component="div" disablePadding>
                                    {item.subItems
                                      .map(subKey => NAV_ITEMS[subKey])
                                      .filter(subItem => {
                                        if (!subItem.permissions || subItem.permissions.length === 0) return true;
                                        return hasAnyPermission(subItem.permissions);
                                      })
                                      .map(subItem => {
                                        const isSubSelected = location.pathname === subItem.path;
                                        return (
                                          <ListItemButton
                                            key={subItem.key}
                                            selected={isSubSelected}
                                            onClick={() => {
                                              if (subItem.comingSoon) {
                                                setComingSoonOpen(true);
                                              } else {
                                                navigate(subItem.path);
                                              }
                                            }}
                                            sx={{
                                              minHeight: 40,
                                              mx: 1.5,
                                              my: 0.2,
                                              borderRadius: 2,
                                              pl: sidebarOpen ? 5 : 1.5,
                                              pr: sidebarOpen ? 2 : 1.5,
                                              justifyContent: sidebarOpen ? 'initial' : 'center',
                                              opacity: subItem.comingSoon ? 0.6 : 1,
                                              cursor: subItem.comingSoon ? 'not-allowed' : 'pointer',
                                              color: isSubSelected ? 'primary.main' : 'text.secondary',
                                              bgcolor: isSubSelected
                                                ? (mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.08)')
                                                : 'transparent',
                                              '&:hover': {
                                                bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                                                color: 'text.primary'
                                              },
                                              transition: 'all 0.2s'
                                            }}
                                          >
                                            <ListItemIcon
                                              sx={{
                                                minWidth: 0,
                                                mr: sidebarOpen ? 2 : 'auto',
                                                justifyContent: 'center',
                                                color: isSubSelected ? 'primary.main' : 'text.secondary',
                                                transform: 'scale(0.9)'
                                              }}
                                            >
                                              {subItem.icon}
                                            </ListItemIcon>
                                            {sidebarOpen && (
                                              <ListItemText
                                                primary={subItem.text}
                                                primaryTypographyProps={{
                                                  fontSize: '0.8125rem',
                                                  fontWeight: isSubSelected ? 600 : 500,
                                                  color: isSubSelected ? 'primary.main' : 'text.primary'
                                                }}
                                              />
                                            )}
                                          </ListItemButton>
                                        );
                                      })}
                                  </List>
                                </Collapse>
                              )
                            }
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </Collapse>
                </Box>
              );
            })}
          </List>

          <Divider sx={{ my: 2, mx: sidebarOpen ? 2 : 1, flex: '0 0 auto' }} />

          <List sx={{ px: sidebarOpen ? 2 : 1, flex: '0 0 auto', mt: 'auto' }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  minHeight: 48,
                  px: sidebarOpen ? 2 : 1.5,
                  color: 'error.main',
                  '&:hover': {
                    bgcolor: 'error.dark',
                    color: 'white',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarOpen ? 40 : 0,
                    color: 'inherit',
                    justifyContent: 'center',
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                {sidebarOpen && (
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer >

      <Box
        component="main"
        sx={{
          bgcolor: 'background.default',
          marginTop: '64px',
          minHeight: 'calc(100vh - 64px)',
          flexGrow: 1,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: 0, // Crucial for flex-grow with overflow items
        }}
      >
        <Box sx={{
          flexGrow: 1,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <Outlet key={location.pathname} />
        </Box>
      </Box>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      {
        (user?.role === 'org_admin' || user?.role === 'compliance_manager') && (
          <SidebarCustomizationDialog
            open={customizerOpen}
            onClose={() => setCustomizerOpen(false)}
            menuGroups={portalConfig.menuGroups.map(group => ({
              label: group.label,
              items: group.items.map(key => NAV_ITEMS[key]).filter(Boolean)
            }))}
            onSave={handleSaveSidebarPreferences}
            initialPreferences={sidebarPreferences}
          />
        )
      }
      <Snackbar
        open={comingSoonOpen}
        autoHideDuration={4000}
        onClose={() => setComingSoonOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setComingSoonOpen(false)} severity="info" variant="filled" sx={{ width: '100%' }}>
          Coming soon to MarQ
        </Alert>
      </Snackbar>
      <Snackbar
        open={setupAlertOpen}
        autoHideDuration={5000}
        onClose={() => setSetupAlertOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSetupAlertOpen(false)} severity="warning" variant="filled" sx={{ width: '100%' }}>
          Please complete your onboarding setup to continue using the platform.
        </Alert>
      </Snackbar>
    </Box >
  );
};

export default Layout;

